import React from 'react';
import { Package, Plus, Minus, ShoppingCart } from 'lucide-react';
import { ci, safeUrl } from './_shared';
import { priceSourceKey } from 'core/utils/productDto';
import { quantityStep, sanitizeQuantity } from 'core/utils/units';

// Kopf-Block: Bild links, rechts Bezeichnung, SKU/SAP-Nr., Preis (mit Herkunft),
// Mengen-Stepper, "In den Warenkorb". Immer sichtbar.
const BlockHeader = ({ dto, t, isDarkMode, quantity, setQuantity, onAddToCart }) => {
  const c = ci(isDarkMode);
  const { identity, procurement, actions } = dto;
  const price = procurement && procurement.price;
  const unit = (price && price.orderUnit) || identity.baseUnit || 'STK';
  const img = safeUrl(identity.images && identity.images[0]);

  return (
    <section className={`${c.card} p-5`}>
      <div className="flex flex-col md:flex-row gap-5">
        <div className={`md:w-56 flex-shrink-0 rounded-xl flex items-center justify-center min-h-[180px] overflow-hidden ${isDarkMode ? 'bg-gray-800/40 border border-white/10' : 'bg-white border border-slate-200'}`}>
          {img ? (
            <img
              src={img}
              alt={identity.name}
              className="max-w-full max-h-[180px] object-contain"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div style={{ display: img ? 'none' : 'flex' }} className={`items-center justify-center ${isDarkMode ? 'text-white/30' : 'text-slate-300'}`}>
            <Package className="w-16 h-16" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {identity.materialGroup && (
            <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-brand/5 text-brand border border-brand/15">
              {identity.materialGroupLabel || identity.materialGroup}
            </span>
          )}
          <h1 className={`text-xl font-semibold ${c.heading} mt-2 leading-snug`}>{identity.name}</h1>

          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {identity.manufacturer && (
              <span className={`text-xs ${c.label}`}>{t('productDetail.manufacturer')}: <span className={c.value}>{identity.manufacturer}</span></span>
            )}
            {identity.sku && (
              <span className={`text-xs ${c.label}`}>{t('productDetail.sku')}: <span className={`font-mono ${c.value}`}>{identity.sku}</span></span>
            )}
            {identity.sapMaterialNo && (
              <span className={`text-xs ${c.label}`}>{t('productDetail.sapMaterialNo')}: <span className={`font-mono ${c.value}`}>{identity.sapMaterialNo}</span></span>
            )}
            {identity.manufacturerPartNo && (
              <span className={`text-xs ${c.label}`}>{t('productDetail.manufacturerPartNo')}: <span className={`font-mono ${c.value}`}>{identity.manufacturerPartNo}</span></span>
            )}
          </div>
          {identity.description && (
            <p className={`text-sm mt-3 leading-relaxed ${c.sub}`}>{identity.description}</p>
          )}

          {price && (
            <div className="flex items-baseline flex-wrap gap-2 mt-4">
              <span className="text-2xl font-bold text-brand">{parseFloat(price.netPrice || 0).toFixed(2)} {price.currency}</span>
              <span className={`text-sm ${c.label}`}>{t('productDetail.priceNet')} · {t('common.per')} {unit}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand/5 text-brand border border-brand/15">{t(priceSourceKey(price.source))}</span>
            </div>
          )}

          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <div className={`inline-flex items-center rounded-xl overflow-hidden ${isDarkMode ? 'border border-white/15' : 'border border-slate-200'}`}>
              <button aria-label={t('common.decrease')} onClick={() => setQuantity(sanitizeQuantity(Math.max(1, parseFloat(quantity) - 1), unit))} className={`w-9 h-9 flex items-center justify-center ${c.sub}`}><Minus className="w-4 h-4" /></button>
              <input
                type="number"
                min="1"
                step={quantityStep(unit)}
                value={quantity}
                onChange={(e) => setQuantity(sanitizeQuantity(Math.max(1, parseFloat(e.target.value) || 1), unit))}
                className={`w-14 h-9 text-center bg-transparent border-0 focus:outline-none ${c.value} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
              />
              <button aria-label={t('common.increase')} onClick={() => setQuantity(sanitizeQuantity(parseFloat(quantity) + 1, unit))} className={`w-9 h-9 flex items-center justify-center ${c.sub}`}><Plus className="w-4 h-4" /></button>
            </div>
            <span className={`text-xs ${c.label}`}>{unit}</span>
            <button
              onClick={() => onAddToCart(quantity)}
              disabled={actions && actions.addToCartEnabled === false}
              className="inline-flex items-center gap-2 bg-brand hover:bg-[#1D4ED8] text-white rounded-xl px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
            >
              <ShoppingCart className="w-4 h-4" /> {t('productDetail.addToCart')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlockHeader;
